import type * as React from "react";
import { type FC, memo, type ReactElement, useMemo } from "react";
import { type RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { type LinearStep, type LinearSubstep } from "../models";
import SystemLayoutSubstep from "./Substeps/SystemLayoutSubstep";
import { nextStep, previousStep, resetCurrentStep } from "../redux/actions";
import { SubstepType } from "../dtos";
import { GenericTileSubstep } from "../../common/components/GenericTileSubstep/GenericTileSubstep";
import LengthSubstep from "./Substeps/LengthSubstep";
import ClosureSubstep from "./Substeps/ClosureSubstep";
import LubricationSubstep from "./Substeps/LubricationSubstep";
import GuidewaySetStep from "./Substeps/GuidewaySetStep/GuidewaySetStep";
import LinearReset from "./Substeps/LinearReset";
import CarriageDetailsSubstep from "./Substeps/CarriageDetailsSubstep/CarriageDetailsSubstep";
import { IconType } from "../../../common/components/icon/IconType";
import { IconSize } from "../../../common/components/icon/IconSize";
import { Icon } from "../../../common/components/icon/Icon";
import { ConfigurationSummarySubstep } from "../../common/components/ConfigurationSummarySubstep";
import { ConfiguratorType } from "../../ConfiguratorType";
import CarriageLocatingFaceSubstep from "../../Carriage/components/CarriageLocatingFaceSubstep";
import LinearLocatingFaceSubstep from "./Substeps/LinearLocatingFaceSubstep";
import RailLocatingFaceSubstep from "../../Rail/components/RailLocatingFaceSubstep";
import ConfigureCarriageSubstep from "../../Carriage/components/ConfigureCarriageSubstep/ConfigureCarriageSubstep";
import { LinearTranslation } from "../../common/components/LinearTranslation";
import ConfigureRailSubstep from "../../Rail/components/ConfigureRailSubstep/ConfigureRailSubstep";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { ShaftLengthSubstep } from "~/Configurators/Shaft/substeps/ShaftLengthSubstep";
import { ShaftToleranceSubstep } from "~/Configurators/Shaft/substeps/ShaftToleranceSubstep";
import { ShaftMachiningSubstep } from "~/Configurators/Shaft/substeps/ShaftMachiningSubstep/ShaftMachiningSubstep";
import { useNextStepName, usePreviousStepName } from "~/Configurators/common/useRenderStepName";
import { MetricShaftMachiningExtraSubstep } from "~/Configurators/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstep";
import { Alert } from "~/common/m3/Alert/Alert";
import KLLTGuidewaySetStep from "./Substeps/GuidewaySetStep/KLLTGuidewaySetStep";
import KLLTCarriageDetailsSubstep from "./Substeps/CarriageDetailsSubstep/KLLTCarriageDetailsSubstep";

type CsticBuilder = (
  data: LinearSubstep,
  index: number,
  totalAmount: number,
  configuratorType: ConfiguratorType,
) => ReactElement | null;

const Step: FC = () => {
  const steps = useSelector(getSteps);
  const currentStepIndex = useSelector(getCurrentStepIndex);
  const elementDefs = useSelector(getElementDefs);
  const configuratorType = useSelector((state: RootState) => state.linearConfigurator.type!);

  const currentStepData = steps[currentStepIndex];

  const { reset, next: nextClick, previous: previousClick } = useControlButtons();
  const { t } = useTranslation();

  const errorCount = currentStepData.substeps
    .map((substep) => elementDefs[substep])
    .filter((substep) => substep.isLenientInvalid).length;

  const resetLabel = <LinearTranslation translationKey={currentStepData.resetLabel} />;

  // const showConfirmation = currentStepIndex > 1;
  const showConfirmation = currentStepData.showResetConfirmation;

  const nextStepName = useNextStepName();
  const previousStepName = usePreviousStepName();

  return (
    <>
      {currentStepIndex === 6 && (
        <div className="scha__linear-configurator__upper-buttons-area">
          <LinearReset
            label={resetLabel}
            onReset={reset}
            showConfirmation={showConfirmation}
            popupMessage={t("page.linear.fullReset.message")}
          />
        </div>
      )}
      <div className="scha__linear-configurator__semi-steps">
        {createSubsteps(currentStepData, elementDefs, configuratorType)}
      </div>
      {errorCount > 0 && (
        <Alert className="mt-30 w-100" severity="error">
          {t("page.linear.invalidSelection", { count: errorCount })}
        </Alert>
      )}
      {currentStepIndex + 1 !== steps.length && ( //don't show on last step (summary)
        <div className="scha__linear-configurator__buttons-area">
          <LinearReset
            label={resetLabel}
            onReset={reset}
            showConfirmation={showConfirmation}
            popupMessage={t("page.linear.fullReset.message")}
          />

          <Stacker direction={"row"} gap={32}>
            {currentStepIndex > 0 && (
              <Button
                onClick={previousClick}
                color={"secondary"}
                startIcon={<Icon type={IconType.ChevronLeft} size={IconSize.FONT_20} />}
              >
                {previousStepName}
              </Button>
            )}
            {currentStepIndex + 1 < steps.length && (
              <Button onClick={nextClick} endIcon={<Icon type={IconType.ChevronRight} size={IconSize.FONT_20} />}>
                {nextStepName}
              </Button>
            )}
          </Stacker>
        </div>
      )}
    </>
  );
};

export default memo(Step);

const getSteps = (state: RootState) => state.linearConfigurator.steps;
const getCurrentStepIndex = (state: RootState) => state.linearConfigurator.currentStep;

const getElementDefs = (state: RootState) => state.linearConfigurator.substeps;

function useControlButtons() {
  const dispatch = useDispatch();
  return useMemo(() => {
    return {
      reset: (e?: React.MouseEvent) => {
        e?.preventDefault();
        dispatch(resetCurrentStep());
      },
      next: () => dispatch(nextStep()),
      previous: () => dispatch(previousStep()),
    };
  }, [dispatch]);
}

function createSubsteps(stepData: LinearStep, elementDefs: Dict<LinearSubstep>, configuratorType: ConfiguratorType) {
  const builders = stepData.substeps
    .map((elementType) => {
      return {
        elementType,
        builder: getSubstepBuilder(elementType),
      };
    })
    .filter((obj) => !!obj.builder && elementDefs[obj.elementType].isVisible);

  const csticAmount = builders.length;

  return builders.map(({ elementType, builder }, index) =>
    builder!(elementDefs[elementType], index + 1, csticAmount, configuratorType),
  );
}

function getSubstepBuilder(type: SubstepType) {
  // During development we can have cstics defined in step, but not present in cstic list...
  if (!type) {
    return null;
  }

  let builder: CsticBuilder | null | undefined = undefined;
  // Check for special cases
  builder = SPECIAL_SUBSTEPS[type];

  // Not a special case? Build as generic tile substep
  if (!builder) {
    return DEFAULT_BUILDER;
  }
  return builder;
}

const SPECIAL_SUBSTEPS: Dict<CsticBuilder | null> = {
  [SubstepType.SystemLayout]: (data, index, total) => (
    <SystemLayoutSubstep key="system-layout" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.Length]: (data, index, total) => (
    <LengthSubstep key="length" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.LocatingFace]: (data, index, total, configuratorType) => {
    if (configuratorType === ConfiguratorType.System) {
      return <LinearLocatingFaceSubstep key="locating-face" def={data} csticIndex={index} csticAmount={total} />;
    }

    if (configuratorType === ConfiguratorType.Carriage) {
      return <CarriageLocatingFaceSubstep key="locating-face" def={data} csticIndex={index} csticAmount={total} />;
    }

    if (configuratorType === ConfiguratorType.Rail) {
      return <RailLocatingFaceSubstep key="locating-face" def={data} csticIndex={index} csticAmount={total} />;
    }

    return null;
  },
  [SubstepType.Lubrication]: (data, index, total) => (
    <LubricationSubstep key="lubrication" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.Closure]: (data, index, total) => (
    <ClosureSubstep key="closure" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.GuidewaySet]: (data, index, total) => (
    <GuidewaySetStep key="guideway-set" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.KLLTGuidewaySet]: (data, index, total) => (
    <KLLTGuidewaySetStep key="guideway-set" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.CarriageDetails]: (data, index, total) => (
    <CarriageDetailsSubstep key="carriage-details" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.KLLTCarriageDetails]: (data, index, total) => (
    <KLLTCarriageDetailsSubstep key="carriage-details" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.SummaryPage]: (data, index, total) => <ConfigurationSummarySubstep key="summary" def={data} />,

  [SubstepType.ConfigureCarriage]: (data, index, total) => (
    <ConfigureCarriageSubstep key="configure-carriage" def={data} csticIndex={index} csticAmount={total} />
  ),

  [SubstepType.ConfigureRail]: (data, index, total) => (
    <ConfigureRailSubstep key="configure-rail" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftLength]: (data, index, total) => (
    <ShaftLengthSubstep key="shaft-length" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftTolerance]: (data, index, total) => (
    <ShaftToleranceSubstep key="shaft-tolerance" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftMachining]: (data, index, total) => (
    <ShaftMachiningSubstep key="shaft-machining" def={data} csticIndex={index} csticAmount={total} />
  ),
  [SubstepType.ShaftMachiningExtra]: (data, index, total) => (
    <MetricShaftMachiningExtraSubstep key="shaft-machining-extra" def={data} csticIndex={index} csticAmount={total} />
  ),
};

const DEFAULT_BUILDER: CsticBuilder = (data, index, total) => (
  <GenericTileSubstep key={data.name} def={data} csticIndex={index} csticAmount={total} />
);
